import { useState, type ComponentPropsWithoutRef, memo } from 'react';
import {
  RichTextEditor,
  RichTextEditorToolbarSticky,
} from '@/components/RichTextEditor';
import { Editor } from '@tiptap/core';
import { Button } from '@simplywallst/ui-core';
import { useFlag } from '@/features/unleash';
import loadable from '@loadable/component';
import useResizeObserver from 'use-resize-observer';

interface Props
  extends Pick<ComponentPropsWithoutRef<typeof RichTextEditor>, 'onUpdate'> {
  errorMessage?: string;
  onDimissAlert?: () => void;
  defaultContent?: string;
}

const NarrativeEditorSuggestions = loadable(
  () => import('./components/NarrativeEditorSuggestions'),
  {
    resolveComponent: (component) => component.NarrativeEditorSuggestions,
  }
);

const PublicNarrativeRichTextEditorBase = ({
  onUpdate,
  defaultContent,
}: Props) => {
  const flag = useFlag({ name: 'mono-narrative-editor-suggestions' });
  const imageFlag = useFlag({
    name: 'mono-rich-text-editor-images',
  });
  const areSuggestionsEnabled = flag.flagsReady && flag.enabled;

  const [editor, setEditor] = useState<Editor | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { ref: editorContainerRef } = useResizeObserver({
    onResize: ({ height }) => {
      if (!height || !editor) return;
      const { state } = editor;
      const { selection, doc } = state;
      const { from } = selection;
      const lastNode = doc.lastChild;
      const isInLastNode = lastNode && doc.resolve(from).node() === lastNode;

      if (!isInLastNode) return;

      window.scrollTo(0, document.body.scrollHeight);
    },
  });
  return (
    <div
      className="relative min-h-[calc(100dvh-320px)]"
      ref={editorContainerRef}
    >
      <div className="grid h-full bg-p-grey-100 px-x3 pt-x3 lg:rounded-x2">
        <RichTextEditor
          defaultContent={defaultContent}
          onUpdate={onUpdate}
          enableImages={imageFlag.flagsReady && imageFlag.enabled}
          onInit={(_conent, editorInstance) => setEditor(editorInstance)}
          placeholderText={
            <div
              className="grid grid-cols-[max-content] grid-rows-[auto_auto] items-center gap-x1 md:grid-cols-[max-content_max-content] md:grid-rows-[auto]"
              role="note"
            >
              <span className="text-base text-soft">
                What&apos;s the story behind this stock?
              </span>
              {areSuggestionsEnabled && (
                <Button
                  styleType="gentle"
                  onClick={() => setShowSuggestions((isOpen) => !isOpen)}
                >
                  💡 Get Ideas for questions to ask yourself
                </Button>
              )}
            </div>
          }
        >
          <RichTextEditorToolbarSticky className="bottom-x7 lg:bottom-0">
            {areSuggestionsEnabled && editor && (
              <NarrativeEditorSuggestions
                editor={editor}
                onOpen={() => setShowSuggestions(true)}
                onClose={() => setShowSuggestions(false)}
                isOpen={showSuggestions}
              />
            )}
          </RichTextEditorToolbarSticky>
        </RichTextEditor>
      </div>
    </div>
  );
};

export const PublicNarrativeRichTextEditor = memo(
  PublicNarrativeRichTextEditorBase
);
